import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Box,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm } from "react-hook-form";
import { enqueueSnackbar } from "notistack";
import { useSignInStore } from "../../../signin-page/api/stores";
import { Handlers } from "../../api/handlers";
import StyledDialogTitle from "../../../../common/components/dialog/dialog-title";
import AppTextField from "../../../../common/components/app-textfield";
import useUserManagementStore from "../../api/store";
import { useResetPassword } from "../../../users/api/hooks";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, AlertTitle } from "@mui/material";
import useMainStore from "../../../landing-page/api/store";
import { commonButtonProps } from "../../../../common/components/buttons/sxprops";

const PasswordConfirmDialog: React.FC = () => {
  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<{ password: string }>();
  const { showPassword, setShowPassword } = useSignInStore();
  const resetPassword = useResetPassword();

  const { selectedClient } = useMainStore();
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const onFormSubmit = ({ password }: { password: string }) => {
    resetPassword.mutate(
      { oldPassword: password, userId: activeUser!.applicationUserId },
      {
        onSuccess: () => {
          enqueueSnackbar("Password confirmed.", { variant: "success" });
          setResetPassModal(false);
          setUserCredentialsSentModal(true);
          setAnchorEl(null);
        },
        onError: () => {
          setError("password", {
            type: "manual",
            message: "Password is incorrect",
          });

          enqueueSnackbar("Password is incorrect", { variant: "error" });
        },
      },
    );
  };

  const handlers = new Handlers();

  const {
    isResetPassModalOpen,
    setResetPassModal,
    setAnchorEl,
    setUserCredentialsSentModal,
    activeUser,
  } = useUserManagementStore();

  const passwordValue = watch("password");

  return (
    <Dialog
      open={isResetPassModalOpen}
      onClose={handlers.handleCloseResetPassModal}
      slotProps={{
        paper: {
          sx: { borderRadius: "24px" },
        },
      }}
      keepMounted
      maxWidth={"laptop"}
      fullWidth
      aria-describedby="confirm-password-dialog"
    >
      <StyledDialogTitle
        title="Enter password to continue"
        actionType="close"
        handleClose={handlers.handleCloseResetPassModal}
      />

      <DialogContent sx={{ minWidth: "300px" }}>
        <Box
          sx={{
            width: "100%",
            padding: "10px",
            display: "flex",
            flexDirection: "column",

            gap: 1,
          }}
        >
          <Typography fontSize={16} color="primary.main" sx={{ mb: 1 }}>
            We need to confirm first if it’s really you.
          </Typography>
          <Alert
            icon={<InfoOutlinedIcon />}
            severity="info"
            sx={{
              mb: 1,
              borderRadius: "12px",
              backgroundColor: "#EAF4FF",
              color: "#1E3A5F",
              border: "1px solid #90CAF9",
              "& .MuiAlert-icon": {
                color: "#1976d2",
              },
            }}
          >
            <Typography fontWeight={600}>
              Resending invitation email to:
            </Typography>
            {activeUser?.role !== "BizboxUser" &&
              activeUser?.role !== "BizboxAdmin" && (
                <Typography>
                  {selectedClient?.clientBasicInfo.companyName}
                </Typography>
              )}
            <Typography>{activeUser?.email}</Typography>
          </Alert>
          <form onSubmit={handleSubmit(onFormSubmit)} autoComplete="off">
            <AppTextField
              control={control}
              name="password"
              id="password"
              label="Enter Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              slotProps={{
                input: {
                  autoComplete: "new-password",
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ alignItems: "center" }}
                    >
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: "#f44336" }}
                        size="small"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Typography fontSize={12} color="primary.main" sx={{ ml: 2 }}>
              Enter your admin password to confirm this action
            </Typography>
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ pr: 3, pb: 2 }}>
        <Button id="cancel-button" onClick={handlers.handleCloseResetPassModal}>
          Cancel
        </Button>
        <Button
          id="continue-button"
          variant="contained"
          loading={resetPassword.isPending}
          disabled={!passwordValue || resetPassword.isPending}
          onClick={handleSubmit(onFormSubmit)}
          sx={commonButtonProps}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordConfirmDialog;
